import { type FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, useTheme, useMediaQuery, Alert } from "@mui/material";
import CustomDialog from "../../components/CustomDialog";
import BDrawer from "../../components/BDrawer";
import type { CreateCompanyRuleDto } from "../../services/configurationService";
import StyledTextField from "../../components/Fields/Textfield";
import StyledTextArea from "../../components/Fields/TextArea";
import SingleSelect from "../../components/Fields/SingleSelect";
import KeyValueEditor from "../../components/KeyValueEditor";
import { configurationService } from "../../services/configurationService";

/** Props passed from parent */
interface AddEditConfigurationProps {
  open: boolean;
  onClose: () => void;
  editMode?: boolean;
  viewMode?: boolean;
  companyId: string;
  initialData?: {
    id?: string;
    title: string;
    description?: string;
    severity?: string;
    category?: string;
    expected?: Record<string, unknown>;
    detect?: Record<string, unknown>;
    preferred_clause?: string;
    metadata?: Array<Record<string, unknown>>;
  };
  onSave: () => void;
}

const AddEditConfiguration: FC<AddEditConfigurationProps> = ({
  open,
  onClose,
  editMode = false,
  viewMode = false,
  companyId,
  initialData,
  onSave,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Detect screen smaller than lg
  const isMobile = useMediaQuery(theme.breakpoints.down(1200));

  /**
   * Urgency was a free-text field whose only valid values lived in a placeholder
   * hint (audit 2.8.1 — Postel's Law), which guaranteed dirty data. The values are
   * the same lowercase strings the API already receives, so the payload shape is
   * unchanged; only the input is constrained.
   */
  const SEVERITY_OPTIONS = [
    { label: t("configuration.severityOptions.high"), value: "hoog" },
    { label: t("configuration.severityOptions.medium"), value: "gemiddeld" },
    { label: t("configuration.severityOptions.low"), value: "laag" },
  ];

  const [formValues, setFormValues] = useState({
    id: "",
    title: "",
    description: "",
    severity: "",
    category: "",
    preferred_clause: "",
  });

  const [expected, setExpected] = useState<Record<string, unknown>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormValues({
          id: initialData.id || "",
          title: initialData.title,
          description: initialData.description || "",
          severity: initialData.severity || "",
          category: initialData.category || "",
          preferred_clause: initialData.preferred_clause || "",
        });
        setExpected(initialData.expected || {});
      } else {
        setFormValues({
          id: "",
          title: "",
          description: "",
          severity: "",
          category: "",
          preferred_clause: "",
        });
        setExpected({});
      }
      setFormErrors({});
    }
  }, [open, initialData]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.title.trim()) {
      errors.title = t("configuration.titleRequired");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const dto: CreateCompanyRuleDto = {
        title: formValues.title.trim(),
        description: formValues.description.trim() || undefined,
        severity: formValues.severity.trim() || undefined,
        category: formValues.category.trim() || undefined,
        expected: Object.keys(expected).length > 0 ? expected : undefined,
        detect: {},
        preferred_clause: formValues.preferred_clause.trim() || undefined,
        metadata: [],
      };

      if (editMode && initialData?.id) {
        // For updates, include the ID from initialData
        dto.id = initialData.id;
        await configurationService.update(companyId, initialData.id, dto);
      } else {
        // For creates, let backend generate the ID
        await configurationService.create(companyId, dto);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving rule:", error);
      setFormErrors({ submit: t("configuration.ruleSaveFailed") });
    } finally {
      setLoading(false);
    }
  };

  const handleExpectedChange = (data: Record<string, unknown>) => {
    setExpected(data);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.expected;
      return newErrors;
    });
  };



  const content = (
    <Box
      height={isMobile ? "calc(100dvh - 250px)" : "auto"}
      overflow={isMobile ? "auto" : "visible"}
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        gap: 2,
        pt: isMobile ? 1 : 0, // Add padding-top on mobile to prevent label clipping
        pb: isMobile ? 1 : 0, // Add padding-bottom for better spacing
      }}
    >
      <StyledTextField
        label={t("configuration.title")}
        value={formValues.title}
        onChange={(e) => {
          setFormValues((prev) => ({ ...prev, title: e.target.value }));
          setFormErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.title;
            return newErrors;
          });
        }}
        error={!!formErrors.title}
        helperText={formErrors.title}
        required
        mode="light"
        disabled={viewMode}
        InputProps={{
          readOnly: viewMode,
        }}
      />

      <StyledTextArea
        label={t("companies.description")}
        value={formValues.description}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            description: e.target.value,
          }))
        }
        rows={3}
        mode="light"
        disabled={viewMode}
        InputProps={{
          readOnly: viewMode,
        }}
      />

      <SingleSelect
        label={t("configuration.severity")}
        options={SEVERITY_OPTIONS}
        value={formValues.severity}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            severity: String(e.target.value ?? ""),
          }))
        }
        placeholder={t("configuration.selectSeverityPlaceholder")}
        mode="light"
        disabled={viewMode}
      />

      <StyledTextField
        label={t("configuration.category")}
        value={formValues.category}
        onChange={(e) =>
          setFormValues((prev) => ({ ...prev, category: e.target.value }))
        }
        mode="light"
        disabled={viewMode}
        InputProps={{
          readOnly: viewMode,
        }}
      />

      <StyledTextField
        label={t("configuration.preferredClause")}
        value={formValues.preferred_clause}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            preferred_clause: e.target.value,
          }))
        }
        mode="light"
        disabled={viewMode}
        InputProps={{
          readOnly: viewMode,
        }}
      />

      <KeyValueEditor
        value={expected}
        onChange={handleExpectedChange}
        label={t("configuration.rulesTitle")}
        placeholder={t("configuration.ruleValuePlaceholder")}
        readOnly={viewMode}
      />
      {formErrors.expected && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {formErrors.expected}
        </Alert>
      )}

      {formErrors.submit && <Alert severity="error">{formErrors.submit}</Alert>}
    </Box>
  );

  const actions = (
    <>
      <Button variant="outlined" fullWidth color="inherit" onClick={onClose}>
        {viewMode ? t("common.close") : t("common.cancel")}
      </Button>
      {!viewMode && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? t("common.saving") : editMode ? t("configuration.update") : t("configuration.create")}
        </Button>
      )}
    </>
  );

  if (isMobile) {
    return (
      <BDrawer
        open={open}
        onClose={onClose}
        title={viewMode ? t("configuration.viewRuleTitle") : editMode ? t("configuration.editRuleTitle") : t("configuration.addRuleTitle")}
        dialogActions={actions}
        showCloseButton
      >
        {content}
      </BDrawer>
    );
  }

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      title={viewMode ? t("configuration.viewRuleTitle") : editMode ? t("configuration.editRuleTitle") : t("configuration.addRuleTitle")}
      actions={actions}
      maxWidth="md"
    >
      {content}
    </CustomDialog>
  );
};

export default AddEditConfiguration;
